import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type FormEvent } from 'react';
import { api } from '../lib/api';
import { requireAccess } from '../lib/guards';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { PageHeader } from '../components/ui/PageHeader';
import type { KundeTyp } from '../lib/api-enums';

interface Kunde {
  id: string;
  kundennummer: string;
  typ: KundeTyp;
}

interface CreatePayload {
  kundennummer?: string;
  typ: KundeTyp;
  firmenname?: string;
  umsatzsteuerId?: string;
  steuernummer?: string;
  handelsregister?: string;
  rechtsform?: string;
  kategorie?: string;
  quelle?: string;
  anrede?: string;
  titel?: string;
  vorname?: string;
  nachname?: string;
  geburtsdatum?: string;
  bankIban?: string;
  bankBic?: string;
  bankKontoinhaber?: string;
  zahlungszielTage: number;
  skontoProzent?: string;
  skontoTage?: number;
  notizen?: string;
}

export const Route = createFileRoute('/kunden/neu')({
  beforeLoad: () => requireAccess('/api/v1/kunden'),
  component: KundeNeuPage,
});

function KundeNeuPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [typ, setTyp] = useState<KundeTyp>('firma');
  const [kundennummer, setKundennummer] = useState('');
  // Firma
  const [firmenname, setFirmenname] = useState('');
  const [umsatzsteuerId, setUmsatzsteuerId] = useState('');
  const [steuernummer, setSteuernummer] = useState('');
  const [handelsregister, setHandelsregister] = useState('');
  const [rechtsform, setRechtsform] = useState('');
  // Privat
  const [anrede, setAnrede] = useState('');
  const [titel, setTitel] = useState('');
  const [vorname, setVorname] = useState('');
  const [nachname, setNachname] = useState('');
  const [geburtsdatum, setGeburtsdatum] = useState('');
  // Querschnitt
  const [kategorie, setKategorie] = useState('');
  const [quelle, setQuelle] = useState('');
  // Bank
  const [bankIban, setBankIban] = useState('');
  const [bankBic, setBankBic] = useState('');
  const [bankKontoinhaber, setBankKontoinhaber] = useState('');
  // Zahlung
  const [zahlungszielTage, setZahlungszielTage] = useState(14);
  const [skontoProzent, setSkontoProzent] = useState('');
  const [skontoTage, setSkontoTage] = useState('');
  // Notizen
  const [notizen, setNotizen] = useState('');

  const [error, setError] = useState<unknown>(null);

  const pflichtfeldFehlt = useMemo(() => {
    if (typ === 'firma' && !firmenname.trim()) return 'Firmenname ist Pflicht bei Typ Firma';
    if (typ === 'privat' && !nachname.trim()) return 'Nachname ist Pflicht bei Typ Privat';
    return null;
  }, [typ, firmenname, nachname]);

  const save = useMutation({
    mutationFn: async () => {
      const body: CreatePayload = {
        typ,
        zahlungszielTage,
      };
      if (kundennummer.trim()) body.kundennummer = kundennummer.trim();
      if (typ === 'firma') {
        body.firmenname = firmenname.trim();
        if (umsatzsteuerId.trim()) body.umsatzsteuerId = umsatzsteuerId.trim();
        if (steuernummer.trim()) body.steuernummer = steuernummer.trim();
        if (handelsregister.trim()) body.handelsregister = handelsregister.trim();
        if (rechtsform.trim()) body.rechtsform = rechtsform.trim();
      } else {
        body.nachname = nachname.trim();
        if (anrede.trim()) body.anrede = anrede.trim();
        if (titel.trim()) body.titel = titel.trim();
        if (vorname.trim()) body.vorname = vorname.trim();
        if (geburtsdatum.trim()) body.geburtsdatum = geburtsdatum.trim();
      }
      if (kategorie.trim()) body.kategorie = kategorie.trim();
      if (quelle.trim()) body.quelle = quelle.trim();
      if (bankIban.trim()) body.bankIban = bankIban.trim();
      if (bankBic.trim()) body.bankBic = bankBic.trim();
      if (bankKontoinhaber.trim()) body.bankKontoinhaber = bankKontoinhaber.trim();
      if (skontoProzent.trim()) body.skontoProzent = skontoProzent.trim();
      if (skontoTage.trim()) body.skontoTage = Number(skontoTage);
      if (notizen.trim()) body.notizen = notizen.trim();
      return api<{ item: Kunde }>('/api/v1/kunden', { method: 'POST', body });
    },
    onSuccess: async (res) => {
      await qc.invalidateQueries({ queryKey: ['kunden'] });
      void navigate({ to: '/kunden/$id', params: { id: res.item.id } });
    },
    onError: (err) => setError(err),
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (pflichtfeldFehlt) {
      setError(new Error(pflichtfeldFehlt));
      return;
    }
    save.mutate();
  }

  return (
    <div className="space-y-4">
      <div>
        <button
          type="button"
          onClick={() => void navigate({ to: '/kunden' })}
          className="text-sm text-brand-700 hover:underline"
        >
          ← zurück zur Kundenliste
        </button>
      </div>
      <PageHeader
        title="Neuer Kunde"
        description="Firmen- oder Privatkunde anlegen. Pflichtfelder hängen vom Typ ab."
      />

      <form
        onSubmit={handleSubmit}
        className="space-y-6 rounded-lg border border-slate-200 bg-white p-6 shadow-sm"
      >
        {/* Typ-Toggle */}
        <fieldset className="space-y-2">
          <legend className="text-sm font-semibold text-slate-900">Typ</legend>
          <div className="flex gap-2" role="radiogroup" aria-label="Kundentyp">
            <TypButton aktiv={typ === 'firma'} onClick={() => setTyp('firma')} label="Firma" />
            <TypButton aktiv={typ === 'privat'} onClick={() => setTyp('privat')} label="Privat" />
          </div>
        </fieldset>

        <Section title="Stammdaten">
          <Field label="Kundennummer">
            <Input
              value={kundennummer}
              onChange={(e) => setKundennummer(e.target.value)}
              placeholder="wird automatisch vergeben wenn leer"
            />
          </Field>

          {typ === 'firma' ? (
            <>
              <Field label="Firmenname *" wide>
                <Input
                  required
                  value={firmenname}
                  onChange={(e) => setFirmenname(e.target.value)}
                />
              </Field>
              <Field label="USt-ID">
                <Input
                  value={umsatzsteuerId}
                  onChange={(e) => setUmsatzsteuerId(e.target.value)}
                  placeholder="DE123456789"
                />
              </Field>
              <Field label="Steuernummer">
                <Input value={steuernummer} onChange={(e) => setSteuernummer(e.target.value)} />
              </Field>
              <Field label="Handelsregister">
                <Input
                  value={handelsregister}
                  onChange={(e) => setHandelsregister(e.target.value)}
                  placeholder="HRB 12345 Frankfurt"
                />
              </Field>
              <Field label="Rechtsform">
                <Input
                  value={rechtsform}
                  onChange={(e) => setRechtsform(e.target.value)}
                  placeholder="GmbH, e.K., …"
                />
              </Field>
            </>
          ) : (
            <>
              <Field label="Anrede">
                <Select value={anrede} onChange={(e) => setAnrede(e.target.value)}>
                  <option value="">–</option>
                  <option value="Herr">Herr</option>
                  <option value="Frau">Frau</option>
                  <option value="Divers">Divers</option>
                </Select>
              </Field>
              <Field label="Titel">
                <Input
                  value={titel}
                  onChange={(e) => setTitel(e.target.value)}
                  placeholder="Dr., Prof., …"
                />
              </Field>
              <Field label="Vorname">
                <Input value={vorname} onChange={(e) => setVorname(e.target.value)} />
              </Field>
              <Field label="Nachname *">
                <Input required value={nachname} onChange={(e) => setNachname(e.target.value)} />
              </Field>
              <Field label="Geburtsdatum">
                <Input
                  type="date"
                  value={geburtsdatum}
                  onChange={(e) => setGeburtsdatum(e.target.value)}
                />
              </Field>
            </>
          )}

          <Field label="Kategorie">
            <Input
              value={kategorie}
              onChange={(e) => setKategorie(e.target.value)}
              placeholder="A, B, C oder frei"
            />
          </Field>
          <Field label="Quelle">
            <Input
              value={quelle}
              onChange={(e) => setQuelle(e.target.value)}
              placeholder="Empfehlung, Web, Messe, …"
            />
          </Field>
        </Section>

        <Section title="Bank">
          <Field label="IBAN" wide>
            <Input
              value={bankIban}
              onChange={(e) => setBankIban(e.target.value)}
              placeholder="DE12 …"
            />
          </Field>
          <Field label="BIC">
            <Input value={bankBic} onChange={(e) => setBankBic(e.target.value)} />
          </Field>
          <Field label="Kontoinhaber">
            <Input value={bankKontoinhaber} onChange={(e) => setBankKontoinhaber(e.target.value)} />
          </Field>
        </Section>

        <Section title="Zahlungsbedingungen">
          <Field label="Zahlungsziel (Tage)">
            <Input
              type="number"
              min={0}
              max={365}
              value={zahlungszielTage}
              onChange={(e) => setZahlungszielTage(Number(e.target.value))}
            />
          </Field>
          <Field label="Skonto %">
            <Input
              type="number"
              step="0.01"
              min={0}
              value={skontoProzent}
              onChange={(e) => setSkontoProzent(e.target.value)}
              placeholder="2.00"
            />
          </Field>
          <Field label="Skonto-Frist (Tage)">
            <Input
              type="number"
              min={0}
              value={skontoTage}
              onChange={(e) => setSkontoTage(e.target.value)}
              placeholder="7"
            />
          </Field>
        </Section>

        <Section title="Notizen">
          <div className="md:col-span-2">
            <textarea
              value={notizen}
              onChange={(e) => setNotizen(e.target.value)}
              rows={3}
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </div>
        </Section>

        <ErrorBanner error={error} />

        <div className="flex items-center justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => void navigate({ to: '/kunden' })}
            disabled={save.isPending}
          >
            Abbrechen
          </Button>
          <Button type="submit" disabled={save.isPending || pflichtfeldFehlt !== null}>
            {save.isPending ? 'Speichere…' : 'Kunde anlegen'}
          </Button>
        </div>
      </form>
    </div>
  );
}

function TypButton({
  aktiv,
  onClick,
  label,
}: {
  aktiv: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={aktiv}
      onClick={onClick}
      className={`inline-flex items-center rounded-md border px-4 py-2 text-sm font-medium transition-colors ${
        aktiv
          ? 'border-brand-600 bg-brand-600 text-white'
          : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
      }`}
    >
      {label}
    </button>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">{children}</div>
    </section>
  );
}

function Field({
  label,
  wide,
  children,
}: {
  label: string;
  wide?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={wide ? 'md:col-span-2' : ''}>
      <label className="text-xs font-medium text-slate-600">{label}</label>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}
